import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

import SegmentEditor from "../SegmentEditor";
import SnapsWiseAudio from "./SnapsWiseAudio";
import SentencePracticeBlock from "./SentencePracticeBlock";
import PracticeControlsPanel from "./PracticeControlsPanel";
import IntonationComparisonPanel from "./IntonationComparisonPanel";
import AIAssessmentPanel from "./AIAssessmentPanel";
import RateMeOverlay from "./RateMeOverlay";
import WriteEchoModePanel from "./WriteEchoModePanel";

const PracticeModeView = ({
  audioref,
  fullaudio,
  timeUpdate,
  chapterTitle,
  segments,
  segment,
  modelSize,
  renderHeaderIdentity,
  renderGuidePill,
  renderSentenceSharePill,
  renderCommentsPill,
  isMetadataNoHelp,
  setShowExerciseHelp,
  handlePreviousSegment,
  handleNextSegment,
  handleSelectSegment,
  showCenterCue,
  practiceMode,
  echoingPhase,
  isCueActive,
  recordingStatus,
  cuePulseCount,
  echoingPulseCount,
  currentWord,
  handleWordPlay,
  handlePlayFromWord,
  handleRangePlay,
  openWordMenu,
  isgranted,
  getMicrophonePermission,
  isPlayingSegment,
  recordingProgress,
  handlePlayPause,
  handleMainAction,
  audioFragmentBlocked = false,
  audioFragmentStatusText = "Preparing audio…",
  getButtonConfig,
  rate,
  setRate,
  loading,
  assessment,
  userRecordingUrl,
  showIntonationVisualization,
  showIntonationHelp,
  setShowIntonationHelp,
  subject,
  file,
  handleLaneWordClick,
  broadcastTime,
  visualizationView,
  auth,
  resolvedAssessmentAudioUrl,
  resolvedNarratorAssessmentAudioUrl,
  canUsePronunciationApi,
  onWriteEchoStageChange,
}) => {
  const [editedSegments, setEditedSegments] = useState(null);
  const [editedSegment, setEditedSegment] = useState(null);
  const [showRateMeOverlay, setShowRateMeOverlay] = useState(false);
  const [recordingChecked, setRecordingChecked] = useState(false);
  const [isPlayingReview, setIsPlayingReview] = useState(false);
  const [writeEchoStage, setWriteEchoStage] = useState("write");
  const [writeEchoShowText, setWriteEchoShowText] = useState(false);
  const [isShortViewport, setIsShortViewport] = useState(false);
  const [visualViewportHeight, setVisualViewportHeight] = useState(() => {
    if (typeof window === "undefined") return 900;
    return Math.round(window.visualViewport?.height || window.innerHeight || 900);
  });
  const [showUserResultFirst, setShowUserResultFirst] = useState(false);

  const isWriteEcho = practiceMode === "writeEcho";
  const effectivePracticeMode = isWriteEcho ? "echoing" : practiceMode;
  const showTargetText =
    !isWriteEcho ||
    writeEchoStage === "result" ||
    (writeEchoStage === "echo" && writeEchoShowText);
  const showAssessmentResults = !isWriteEcho || writeEchoStage === "result";
  const showMainWordLane =
    !isWriteEcho || writeEchoStage === "echo" || writeEchoStage === "result";

  useEffect(() => {
    const viewport = window.visualViewport;
    let frame = null;

    const updateVisualViewport = () => {
      if (frame != null) window.cancelAnimationFrame(frame);

      frame = window.requestAnimationFrame(() => {
        setVisualViewportHeight(
          Math.round(viewport?.height || window.innerHeight || 900)
        );
      });
    };

    updateVisualViewport();
    window.addEventListener("resize", updateVisualViewport, { passive: true });
    viewport?.addEventListener("resize", updateVisualViewport);
    viewport?.addEventListener("scroll", updateVisualViewport);

    return () => {
      if (frame != null) window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", updateVisualViewport);
      viewport?.removeEventListener("resize", updateVisualViewport);
      viewport?.removeEventListener("scroll", updateVisualViewport);
    };
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-height: 900px)");

    const updateViewportMode = () => {
      setIsShortViewport(mediaQuery.matches);
    };

    updateViewportMode();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateViewportMode);
    } else {
      mediaQuery.addListener(updateViewportMode);
    }

    return () => {
      if (typeof mediaQuery.removeEventListener === "function") {
        mediaQuery.removeEventListener("change", updateViewportMode);
      } else {
        mediaQuery.removeListener(updateViewportMode);
      }
    };
  }, []);

  useEffect(() => {
    setRecordingChecked(false);
    setShowRateMeOverlay(false);
    setIsPlayingReview(false);
    setShowUserResultFirst(
      Boolean(assessment && !assessment.error)
    );
  }, [assessment]);

  useEffect(() => {
    setWriteEchoStage("write");
    setWriteEchoShowText(false);
    setShowUserResultFirst(false);
    onWriteEchoStageChange?.("write");
  }, [segment?.id, practiceMode]);

  const changeWriteEchoStage = (stage, details = {}) => {
    setWriteEchoStage(stage);

    if (stage === "echo") {
      setWriteEchoShowText(Boolean(details.showText));
    } else if (stage === "write") {
      setWriteEchoShowText(false);
    }

    onWriteEchoStageChange?.(stage);
  };

  const handleSaveEditedSegments = async (editedSegmentsToSave) => {
    const token = localStorage.getItem("token");
    const requestData = {
      author: subject.author,
      title: subject.title,
      language: subject.language,
      type: subject.type,
      owner: subject.owner,
      file: file.file,
      size_of_model: modelSize,
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_USER_API_URL}/whisper/add-edited-segments`,
        { fileinfo: requestData, segments: editedSegmentsToSave },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status === "success") {
        alert(`Edited segments ${response.data.action} successfully!`);
        setEditedSegments(null);
        setEditedSegment(null);
        return true;
      }

      throw new Error("Failed to save edited segments");
    } catch (error) {
      console.error("Error saving edited segments:", error);
      alert("Error saving edited segments. Please try again.");
      throw error;
    }
  };

  const displayedAssessment =
    showAssessmentResults ? assessment : null;

  const showPitchPanel =
    !isWriteEcho ||
    writeEchoStage === "review" ||
    writeEchoStage === "result";

  // Before a result exists, treat the sentence, controls, and narrator graph as
  // one viewport-sized practice workspace. Results intentionally return to the
  // normal document flow because Rate me and assessment content appear then.
  const fitNarratorViewport =
    showPitchPanel &&
    !displayedAssessment &&
    !editedSegments;

  const compactPracticeViewport =
    fitNarratorViewport &&
    (isShortViewport || visualViewportHeight < 760);

  const practiceGraphMinHeight =
    visualViewportHeight < 620
      ? 160
      : visualViewportHeight < 740
      ? 170
      : 180;

  const audioDependentControlsBlocked =
    audioFragmentBlocked &&
    (effectivePracticeMode === "echoing" ||
      effectivePracticeMode === "shadowing");

  const returnToNarratorPractice = () => {
    setShowUserResultFirst(false);
    setShowRateMeOverlay(false);
    setRecordingChecked(false);
    setIsPlayingReview(false);
  };

  const handlePracticePlayPause = () => {
    // Pressing Play means the learner is returning to the reference.
    // Pausing an already playing reference does not need another state change,
    // but applying this unconditionally keeps all playback entry paths aligned.
    returnToNarratorPractice();
    handlePlayPause?.();
  };

  const handlePracticeMainAction = () => {
    // Echoing and shadowing begin from the narrator-reference state.
    returnToNarratorPractice();
    handleMainAction?.();
  };

  const openRateMeReview = () => {
    setRecordingChecked(false);
    setIsPlayingReview(false);
    setShowRateMeOverlay(true);
  };

  const controls = (
    <div
      aria-busy={audioDependentControlsBlocked}
      aria-label={
        audioDependentControlsBlocked
          ? audioFragmentStatusText
          : undefined
      }
    >
      <div
        className={
          audioDependentControlsBlocked
            ? "pointer-events-none select-none opacity-65 saturate-50 transition-[opacity,filter] duration-200"
            : "transition-[opacity,filter] duration-200"
        }
      >
        <PracticeControlsPanel
          isgranted={isgranted}
          getMicrophonePermission={getMicrophonePermission}
          practiceMode={effectivePracticeMode}
          recordingStatus={recordingStatus}
          recordingPhase={
            loading
              ? "processing"
              : recordingStatus === "recording"
              ? "recording"
              : "idle"
          }
          isPlayingSegment={isPlayingSegment}
          recordingProgress={recordingProgress}
          handlePlayPause={handlePracticePlayPause}
          handleMainAction={handlePracticeMainAction}
          getButtonConfig={getButtonConfig}
          rate={rate}
          setRate={setRate}
          audioref={audioref}
          loading={loading}
          echoingPhase={echoingPhase}
          echoingPulseCount={echoingPulseCount}
          shadowCountdownActive={isCueActive}
          shadowPulseCount={cuePulseCount}
          compact={compactPracticeViewport}
        />
      </div>


    </div>
  );

  return (
    <div
      className={[
        "flex-1 h-full min-h-0 min-w-0 overflow-y-auto overflow-x-hidden overscroll-contain",
        compactPracticeViewport
          ? "p-3 xl:p-4 pb-4"
          : "p-4 xl:p-6 pb-6",
      ].join(" ")}
    >
      <SnapsWiseAudio
        audioref={audioref}
        fullaudio={fullaudio}
        timeUpdate={timeUpdate}
      />

      {segments && !editedSegments && (
        <div
          data-practice-viewport={fitNarratorViewport ? "true" : undefined}
          style={
            fitNarratorViewport
              ? {
                  "--practice-graph-min": `${practiceGraphMinHeight}px`,
                }
              : undefined
          }
          className={[
            fitNarratorViewport
              ? [
                  "grid min-h-full min-w-0 content-start",
                  "grid-rows-[max-content_max-content_minmax(var(--practice-graph-min),1fr)]",
                  "gap-3",
                ].join(" ")
              : "flex flex-col gap-6",
          ].join(" ")}
        >
          <div className={fitNarratorViewport ? "min-w-0 self-start" : "shrink-0"}>
          <SentencePracticeBlock
            segment={segment}
            segments={segments}
            assessment={displayedAssessment}
            renderHeaderIdentity={renderHeaderIdentity}
            renderGuidePill={renderGuidePill}
            renderSentenceSharePill={renderSentenceSharePill}
            renderCommentsPill={renderCommentsPill}
            isMetadataNoHelp={isMetadataNoHelp}
            setShowExerciseHelp={setShowExerciseHelp}
            handlePreviousSegment={handlePreviousSegment}
            handleNextSegment={handleNextSegment}
            handleSelectSegment={handleSelectSegment}
            showCenterCue={false}
            practiceMode={effectivePracticeMode}
            echoingPhase={echoingPhase}
            isCueActive={isCueActive}
            recordingStatus={recordingStatus}
            cuePulseCount={cuePulseCount}
            echoingPulseCount={echoingPulseCount}
            currentWord={currentWord}
            handleWordPlay={handleWordPlay}
            handleRangePlay={handleRangePlay}
            openWordMenu={openWordMenu}
            file={file}
            auth={auth}
            showWords={showTargetText}
            enableWordActions={
              showTargetText && (!isWriteEcho || writeEchoStage === "result")
            }
            isPlayingSegment={isPlayingSegment}
            showWordLane={showMainWordLane}
            fitViewport={fitNarratorViewport}
            compactViewport={compactPracticeViewport}
            viewportHeight={visualViewportHeight}
          />
          </div>

          {isWriteEcho ? (
            <WriteEchoModePanel
              targetText={segment?.text || ""}
              words={segment?.words || []}
              currentWord={currentWord}
              isPlayingSegment={isPlayingSegment}
              onPlayReference={
                audioFragmentBlocked
                  ? () => {}
                  : handlePracticePlayPause
              }
              onCueWordPlay={
                audioFragmentBlocked ? () => {} : handlePlayFromWord
              }
              rate={rate}
              setRate={setRate}
              assessment={assessment}
              onStageChange={changeWriteEchoStage}
            >
              {controls}
            </WriteEchoModePanel>
          ) : (
            <div className={fitNarratorViewport ? "self-start" : "shrink-0"}>
              {controls}
            </div>
          )}

          {showPitchPanel && (
            <div
              className={
                fitNarratorViewport
                  ? "flex min-w-0 self-stretch"
                  : ""
              }
              style={
                fitNarratorViewport
                  ? { minHeight: "var(--practice-graph-min)" }
                  : undefined
              }
            >
            <IntonationComparisonPanel
              showIntonationVisualization={showIntonationVisualization}
              showIntonationHelp={showIntonationHelp}
              setShowIntonationHelp={setShowIntonationHelp}
              segment={segment}
              assessment={displayedAssessment}
              subject={subject}
              file={file}
              handleLaneWordClick={showTargetText ? handleLaneWordClick : undefined}
              broadcastTime={broadcastTime}
              audioref={audioref}
              visualizationView={isWriteEcho ? "pitch" : visualizationView}
              rate={rate}
              setRate={setRate}
              auth={auth}
              resolvedAssessmentAudioUrl={resolvedAssessmentAudioUrl}
              resolvedNarratorAssessmentAudioUrl={resolvedNarratorAssessmentAudioUrl}
              fitViewport={fitNarratorViewport}
              compactViewport={compactPracticeViewport}
              userFirst={showUserResultFirst}
              showRateMe={
                showAssessmentResults &&
                Boolean(assessment && !assessment.error) &&
                showUserResultFirst
              }
              onRateMe={openRateMeReview}
            />
            </div>
          )}

          {showAssessmentResults && (
            <AIAssessmentPanel
              assessment={assessment}
              canUsePronunciationApi={canUsePronunciationApi}
              segment={segment}
            />
          )}

          {auth?.role === "ADMIN" && (
            <div className="flex justify-center">
              <button
                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white rounded-lg transition-all duration-300 font-medium shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40"
                onClick={() => {
                  const currentSegment =
                    segments.find((item) => item.id === segment.id) || segments[0];
                  setEditedSegments([...segments]);
                  setEditedSegment({ ...currentSegment });
                }}
              >
                Edit Segments
              </button>
            </div>
          )}
        </div>
      )}

      {editedSegments && (
        <SegmentEditor
          segments={segments}
          editedSegments={editedSegments}
          setEditedSegments={setEditedSegments}
          editedSegment={editedSegment}
          setEditedSegment={setEditedSegment}
          audioref={audioref}
          subject={subject}
          file={file}
          onSave={handleSaveEditedSegments}
          onCancel={() => {
            setEditedSegments(null);
            setEditedSegment(null);
          }}
        />
      )}

      <RateMeOverlay
        open={showRateMeOverlay}
        onClose={() => setShowRateMeOverlay(false)}
        userRecordingUrl={userRecordingUrl}
        recordingChecked={recordingChecked}
        setRecordingChecked={setRecordingChecked}
        isPlayingReview={isPlayingReview}
        setIsPlayingReview={setIsPlayingReview}
        segment={segment}
        assessment={assessment}
        subject={subject}
        file={file}
        chapterTitle={chapterTitle || file?.chapterTitle || file?.title || file?.name || file?.file}
      />
    </div>
  );
};

export default PracticeModeView;